'use client';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectDiscoveryMapCurrentTerritory,
  selectDiscoveryMapMapBounds,
  selectDiscoveryMapHasInvalidBounds,
  extractBoundsParams,
  selectAdvancedSearchRadius,
} from 'src/redux/slices/discoveryMapSlice';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import type { AdvancedSearchExecuteParams } from 'src/api/redux/advancedSearches';
import { useGetAdvancedSearchMetricsQuery } from 'src/api/redux/advancedSearches';

export const useAdvancedSearchQueryParams = (): {
  queryParams: AdvancedSearchExecuteParams | null;
  skip: boolean;
} => {
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const hasInvalidBounds = useSelector(selectDiscoveryMapHasInvalidBounds);
  const advancedSearchRadius = useSelector(selectAdvancedSearchRadius);

  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const { page, pageSize, sort, order, q } = useDiscoveryList();

  const isAdvancedSearch = appliedSavedVolume?.volumeType === 'advancedSearch';

  // Fetch metrics to validate sort — uses RTK Query cache, no extra request
  const advancedSearchId = isAdvancedSearch
    ? appliedSavedVolume?.id
    : undefined;
  const { data: metricsData } = useGetAdvancedSearchMetricsQuery(
    advancedSearchId,
    { skip: !isAdvancedSearch || !advancedSearchId }
  );

  // Only send sort/order when the sort matches a valid metric code
  const validMetricCodes = useMemo(() => {
    if (!metricsData?.results) return new Set<string>();
    const codes = new Set<string>();
    for (const metric of metricsData.results) {
      for (const option of metric.display_options) {
        codes.add(option.code);
      }
    }
    return codes;
  }, [metricsData]);

  const isSortInitialized = validMetricCodes.has(sort);

  const queryParams = useMemo(() => {
    if (!isAdvancedSearch) return null;

    const params: AdvancedSearchExecuteParams = {
      id: appliedSavedVolume.id,
      page,
      pageSize,
      ...(isSortInitialized ? { sort, order } : {}),
      ...(q ? { q } : {}),
    };

    if (advancedSearchRadius) {
      params.center = advancedSearchRadius.center;
      params.radius_miles = advancedSearchRadius.radius_miles;
    } else {
      if (currentTerritory?.id) {
        params.territory_id = currentTerritory.id;
      }
      const boundsParams = extractBoundsParams(mapBounds);
      if (boundsParams) {
        Object.assign(params, boundsParams);
      }
    }

    return params;
  }, [
    isAdvancedSearch,
    appliedSavedVolume,
    currentTerritory?.id,
    mapBounds,
    page,
    pageSize,
    isSortInitialized,
    sort,
    order,
    q,
    advancedSearchRadius,
  ]);

  return {
    queryParams,
    skip: !isAdvancedSearch || !queryParams,
  };
};
